import styled from 'styled-components';
import tw from 'twin.macro';
import { Alert, Field, FormGrid, Input, Muted, Stack, Textarea } from '../../components/ui';
import { CheckRow, MoneyInput, NumberInput, ToggleRow } from './serviceControls';
import { distanceWord } from '../../utils/serviceUnits';
import {
  SUPPLY_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type SuppliesForm,
  type WorkerForm,
  type WorkerFormErrors,
  type WorkerSection,
} from './workerForm';

/**
 * Editors for the worker settings (spec 019 W6, W7): the wizard shows one per
 * step and the manage page one per card, on the same form model.
 */

export interface SectionProps {
  form: WorkerForm;
  errors: WorkerFormErrors;
  onChange: (patch: Partial<WorkerForm>) => void;
  /** City and ZIP of the worker's location, for the hints. */
  locationLabel?: string | null;
}

export function ProfileSection({ form, errors, onChange }: SectionProps) {
  return (
    <Stack $gap={4}>
      <Field
        label="Name customers see"
        htmlFor="worker-name"
        required
        error={errors.displayName}
        hint="A first name or a team name, for example Ana or Sparkle Team 2."
      >
        <Input
          id="worker-name"
          value={form.displayName}
          maxLength={60}
          aria-invalid={!!errors.displayName || undefined}
          onChange={event => onChange({ displayName: event.target.value })}
          data-testid="worker-name"
        />
      </Field>
      <Field
        label="Bio"
        htmlFor="worker-bio"
        error={errors.bio}
        hint="What this worker is good at."
      >
        <Textarea
          id="worker-bio"
          value={form.bio}
          maxLength={1000}
          onChange={event => onChange({ bio: event.target.value })}
          data-testid="worker-bio"
        />
      </Field>
      <FormGrid>
        <Field label="Years of experience" htmlFor="worker-years" error={errors.yearsExperience}>
          <NumberInput
            id="worker-years"
            value={form.yearsExperience}
            invalid={!!errors.yearsExperience}
            onChange={yearsExperience => onChange({ yearsExperience })}
          />
        </Field>
        <Field label="Languages" htmlFor="worker-languages" hint="Separate with commas.">
          <Input
            id="worker-languages"
            value={form.languages}
            placeholder="English, Spanish"
            onChange={event => onChange({ languages: event.target.value })}
          />
        </Field>
      </FormGrid>
    </Stack>
  );
}

export function AreaSection({ form, errors, onChange, locationLabel }: SectionProps) {
  // Spec 028: the worker's own unit.
  const unit = form.distanceUnit ?? 'mi';
  const word = distanceWord(unit);
  const one = distanceWord(unit, false);
  return (
    <Stack $gap={4}>
      <Field
        label="Service radius"
        htmlFor="worker-radius"
        required
        error={errors.serviceRadiusMiles}
        hint={
          locationLabel
            ? `Measured in a straight line from the location in ${locationLabel}.`
            : 'Measured in a straight line from the location.'
        }
      >
        <NumberInput
          id="worker-radius"
          value={form.serviceRadiusMiles}
          trailing={word}
          invalid={!!errors.serviceRadiusMiles}
          onChange={serviceRadiusMiles => onChange({ serviceRadiusMiles })}
          testId="worker-radius"
        />
      </Field>
      <Field
        label={unit === 'km' ? 'Postal areas you also serve' : 'ZIP codes you also serve'}
        htmlFor="worker-zips"
        error={errors.serviceZips}
        hint={
          unit === 'km'
            ? 'Optional. The first three characters of a postal code (M5V). Jobs there count as in your area, even past the radius.'
            : 'Optional. Jobs in these ZIP codes count as in your area, even past the radius.'
        }
      >
        <Input
          id="worker-zips"
          value={form.serviceZips}
          placeholder={unit === 'km' ? 'M5V, M4W' : '85004, 85006'}
          aria-invalid={!!errors.serviceZips || undefined}
          onChange={event => onChange({ serviceZips: event.target.value })}
          data-testid="worker-zips"
        />
      </Field>
      <ToggleRow
        label="Take jobs past the radius"
        hint={`Customers further away pay a travel fee for each extra ${one}.`}
        checked={form.acceptsOutOfRadius}
        onChange={acceptsOutOfRadius => onChange({ acceptsOutOfRadius })}
        testId="worker-out-of-radius"
      />
      {form.acceptsOutOfRadius && (
        <FormGrid>
          <Field
            label={`Price per extra ${one}`}
            htmlFor="worker-per-mile"
            required
            error={errors.perMileBeyondRadius}
          >
            <MoneyInput
              id="worker-per-mile"
              value={form.perMileBeyondRadius}
              invalid={!!errors.perMileBeyondRadius}
              onChange={perMileBeyondRadius => onChange({ perMileBeyondRadius })}
              testId="worker-per-mile"
            />
          </Field>
          <Field
            label="Farthest distance"
            htmlFor="worker-max-travel"
            required
            error={errors.maxTravelMiles}
            hint="Jobs further than this cannot be booked."
          >
            <NumberInput
              id="worker-max-travel"
              value={form.maxTravelMiles}
              trailing={word}
              invalid={!!errors.maxTravelMiles}
              onChange={maxTravelMiles => onChange({ maxTravelMiles })}
              testId="worker-max-travel"
            />
          </Field>
        </FormGrid>
      )}
    </Stack>
  );
}

const DayRow = styled.div`
  ${tw`grid items-center gap-3 py-2`}
  grid-template-columns: minmax(110px, 1fr) 1fr 1fr;
  border-bottom: 1px solid var(--color-border);
  &:last-child {
    border-bottom: none;
  }
`;

export function ScheduleSection({ form, errors, onChange }: SectionProps) {
  const setDay = (
    day: (typeof WEEKDAYS)[number],
    patch: Partial<WorkerForm['workingHours']['monday']>,
  ) =>
    onChange({
      workingHours: { ...form.workingHours, [day]: { ...form.workingHours[day], ...patch } },
    });
  return (
    <Stack $gap={4}>
      <ToggleRow
        label="Use the location's hours"
        hint="The operating hours you set on the location. Turn off to set hours for this worker only."
        checked={form.useLocationHours}
        onChange={useLocationHours => onChange({ useLocationHours })}
        testId="worker-location-hours"
      />
      {!form.useLocationHours && (
        <div data-testid="worker-hours">
          {WEEKDAYS.map(day => (
            <DayRow key={day}>
              <CheckRow
                label={WEEKDAY_LABELS[day]}
                checked={form.workingHours[day].available}
                onChange={available => setDay(day, { available })}
              />
              <Input
                type="time"
                step={1800}
                aria-label={`${WEEKDAY_LABELS[day]} opens`}
                value={form.workingHours[day].start}
                disabled={!form.workingHours[day].available}
                onChange={event => setDay(day, { start: event.target.value })}
              />
              <Input
                type="time"
                step={1800}
                aria-label={`${WEEKDAY_LABELS[day]} closes`}
                value={form.workingHours[day].end}
                disabled={!form.workingHours[day].available}
                onChange={event => setDay(day, { end: event.target.value })}
              />
            </DayRow>
          ))}
          {errors.workingHours && (
            <Alert tone="error" title="Hours">
              {errors.workingHours}
            </Alert>
          )}
        </div>
      )}
      <FormGrid>
        <Field
          label="Buffer after each job"
          htmlFor="worker-buffer"
          required
          error={errors.bufferHours}
          hint="0 to 6 hours. The worker cannot be booked right after a job's window."
        >
          <NumberInput
            id="worker-buffer"
            value={form.bufferHours}
            trailing="hours"
            invalid={!!errors.bufferHours}
            onChange={bufferHours => onChange({ bufferHours })}
            testId="worker-buffer"
          />
        </Field>
        <Field
          label="Advance notice"
          htmlFor="worker-notice"
          required
          error={errors.advanceNoticeHours}
          hint="1 to 72 hours before the window starts."
        >
          <NumberInput
            id="worker-notice"
            value={form.advanceNoticeHours}
            trailing="hours"
            invalid={!!errors.advanceNoticeHours}
            onChange={advanceNoticeHours => onChange({ advanceNoticeHours })}
            testId="worker-notice"
          />
        </Field>
      </FormGrid>
    </Stack>
  );
}

export function SuppliesSection({ form, onChange }: SectionProps) {
  const set = (key: keyof SuppliesForm, value: boolean) =>
    onChange({ supplies: { ...form.supplies, [key]: value } });
  return (
    <Stack $gap={4}>
      <Muted $size="sm">
        Customers see what you bring. When neither side has something, we tell the customer before
        they book.
      </Muted>
      <div data-testid="worker-supplies">
        {(Object.keys(SUPPLY_LABELS) as (keyof SuppliesForm)[]).map(key => (
          <CheckRow
            key={key}
            label={SUPPLY_LABELS[key]}
            checked={form.supplies[key]}
            onChange={value => set(key, value)}
            testId={`worker-supply-${key}`}
          />
        ))}
      </div>
    </Stack>
  );
}

export function BookingSection({ form, errors, onChange }: SectionProps) {
  return (
    <Stack $gap={4}>
      <ToggleRow
        label="Instant book"
        hint="Customers book without waiting for you. The job is confirmed right away."
        checked={form.instantBook}
        onChange={instantBook => onChange({ instantBook })}
        testId="worker-instant-book"
      />
      {!form.instantBook && (
        <Field
          label="Confirm each request within"
          htmlFor="worker-confirm-window"
          required
          error={errors.confirmWindowHours}
          hint="1 to 24 hours. A request you do not answer in time is cancelled and the customer is not charged."
        >
          <NumberInput
            id="worker-confirm-window"
            value={form.confirmWindowHours}
            trailing="hours"
            invalid={!!errors.confirmWindowHours}
            onChange={confirmWindowHours => onChange({ confirmWindowHours })}
            testId="worker-confirm-window"
          />
        </Field>
      )}
    </Stack>
  );
}

export function WorkerSectionFields({
  section,
  ...props
}: SectionProps & { section: WorkerSection }) {
  switch (section) {
    case 'profile':
      return <ProfileSection {...props} />;
    case 'area':
      return <AreaSection {...props} />;
    case 'schedule':
      return <ScheduleSection {...props} />;
    case 'supplies':
      return <SuppliesSection {...props} />;
    case 'booking':
      return <BookingSection {...props} />;
    default:
      return null;
  }
}
